import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';

import { ApiCustomerService } from '../../data/services/api/api-customer.service';

interface UserOption { label: string; value: string; }

/**
 * Modal Admin-only para reasignar el vendedor interno (seller) y/o el broker que trajo al
 * cliente (broker). `referredBy` se muestra solo-lectura (inmutable). Autocontenido:
 * carga sus propios pickers (Seller / Broker+MasterBroker), su form y su toast.
 */
@Component({
  selector: 'app-reassign-owner-modal',
  templateUrl: './reassign-owner-modal.component.html',
  styleUrls: ['./reassign-owner-modal.component.scss'],
  providers: [MessageService],
})
export class ReassignOwnerModalComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() clientId = '';
  @Input() client: any = null;
  @Output() reassigned = new EventEmitter<void>();

  sellers: UserOption[] = [];
  brokers: UserOption[] = [];
  loadingUsers = false;
  saving = false;

  form: FormGroup;
  private initialSellerId: string | null = null;
  private initialBrokerId: string | null = null;

  constructor(private api: ApiCustomerService, private messageService: MessageService, private fb: FormBuilder) {
    this.form = this.fb.group({ sellerId: [null as string | null], brokerId: [null as string | null] });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.prefill();
      this.loadUsers();
    }
  }

  private refId(ref: any): string | null {
    if (!ref) return null;
    return typeof ref === 'object' ? (ref._id ?? null) : ref;
  }

  private prefill(): void {
    this.initialSellerId = this.refId(this.client?.seller);
    this.initialBrokerId = this.refId(this.client?.broker);
    this.form.reset({ sellerId: this.initialSellerId, brokerId: this.initialBrokerId });
  }

  private toOptions(users: any[]): UserOption[] {
    return (users || []).map((u) => ({ value: u._id, label: [u.names, u.lastNames].filter(Boolean).join(' ').trim() || u.email || u._id }));
  }

  private loadUsers(): void {
    this.loadingUsers = true;
    // Un solo fetch de usuarios internos; se parten client-side por rol.
    // Vendedores = rol Seller. Brokers = Broker + MasterBroker. Se excluyen bloqueados.
    this.api.getInternalUsers().subscribe((users: any[]) => {
      const usable = (users || []).filter((u: any) => u?.status !== 'BLOCKED' && u?.status !== 'BLOCKED_BY_CASCADE');
      this.sellers = this.toOptions(usable.filter((u: any) => u?.role?.name === 'Seller'));
      this.brokers = this.toOptions(usable.filter((u: any) => u?.role?.name === 'Broker' || u?.role?.name === 'MasterBroker'));
      this.loadingUsers = false;
    });
  }

  private nameOf(ref: any): string | null {
    if (!ref || typeof ref !== 'object') return null;
    return [ref.names, ref.lastNames].filter(Boolean).join(' ').trim() || null;
  }

  get currentSellerName(): string | null { return this.nameOf(this.client?.seller); }
  get currentBrokerName(): string | null { return this.nameOf(this.client?.broker); }
  get referredByName(): string | null { return this.nameOf(this.client?.referredBy); }

  get hasChanges(): boolean {
    return (this.form.value.sellerId ?? null) !== this.initialSellerId || (this.form.value.brokerId ?? null) !== this.initialBrokerId;
  }

  onVisibleChange(v: boolean): void {
    this.visible = v;
    this.visibleChange.emit(v);
  }

  close(): void {
    this.onVisibleChange(false);
  }

  save(): void {
    const sellerId = this.form.value.sellerId ?? null;
    const brokerId = this.form.value.brokerId ?? null;
    const payload: { seller?: string; broker?: string | null } = {};
    if (sellerId !== this.initialSellerId && sellerId) payload.seller = sellerId;
    if (brokerId !== this.initialBrokerId) payload.broker = brokerId; // null limpia el broker
    if (!Object.keys(payload).length) { this.close(); return; }

    this.saving = true;
    this.api.reassign(this.clientId, payload).subscribe((r: any) => {
      this.saving = false;
      if (!r.error) {
        this.messageService.add({ severity: 'success', summary: 'Reasignado', detail: 'Se actualizó la asignación del cliente.', life: 3000 });
        this.reassigned.emit();
        this.close();
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: r.msg ?? 'No se pudo reasignar el cliente', life: 4000 });
      }
    });
  }
}
